import moment from 'moment'
import { getMissionType } from 'Containers/Campaigns/utils'
import { CAMPAIGN_ENUM_TYPES, MONTHS_LIST } from '../../../shared/constants'
import { escapeCsvString } from '../../../shared/csv'

export const formatNumber = price =>
  Number(price.toFixed(2)).toLocaleString('en')
const formatCSVvalue = value =>
  escapeCsvString(Number(value.toFixed(2)).toLocaleString('en'))

export const getYearlyCampaignsData = campaigns => {
  const completedMissionsList = campaigns.reduce(
    (acc, { type, missions }) =>
      type === CAMPAIGN_ENUM_TYPES.HANDTOHAND ||
      type === CAMPAIGN_ENUM_TYPES.LETTERBOX
        ? {
            ...acc,
            [type]: acc[type] ? [...acc[type], ...missions] : missions,
          }
        : acc,
    {},
  )

  const completedMissionsGroupedByMonth = Object.entries(
    completedMissionsList,
  ).reduce((acc, [type, missions]) => {
    const missionsGroupedByMonthAndYear = missions.reduce((acc, mission) => {
      const year = Number(mission.dateTime)
        ? moment(Number(mission.dateTime)).format('YYYY')
        : moment(mission.dateTime).format('YYYY')
      const month = Number(mission.dateTime)
        ? moment(Number(mission.dateTime)).format('M')
        : moment(mission.dateTime).format('M')
      if (acc[year]) {
        if (acc[year][month]) {
          return {
            ...acc,
            [year]: {
              ...acc[year],
              [month]: [...acc[year][month], mission],
            },
          }
        }
        return { ...acc, [year]: { ...acc[year], [month]: [mission] } }
      }
      return { ...acc, [year]: { [month]: [mission] } }
    }, {})

    return {
      ...acc,
      [type]: Object.keys(missionsGroupedByMonthAndYear)
        .sort((a, b) => Number(b) - Number(a))
        .reduce((acc, year) => {
          acc[`${year}y`] = Object.keys(missionsGroupedByMonthAndYear[year])
            .sort((a, b) => Number(b) - Number(a))
            .reduce((acc, month) => {
              acc[MONTHS_LIST[Number(month) - 1]] =
                missionsGroupedByMonthAndYear[year][month]
              return acc
            }, {})
          return acc
        }, {}),
    }
  }, {})

  const metricsValuesForGroupedMissions = Object.entries(
    completedMissionsGroupedByMonth,
  ).reduce((acc, [type, years]) => {
    const yearlyMetricsData = Object.entries(years).reduce(
      (acc, [year, months]) => {
        const monthlyMetricsData = Object.entries(months).reduce(
          (acc, [month, missions]) => {
            const metricsList = missions.reduce((acc, { metrics }) => {
              metrics.forEach(({ metricId: id, isPrimary, name }) => {
                if (acc.find(metric => metric.id === id)) return
                return acc.push({
                  id,
                  isPrimary,
                  name,
                })
              })
              return acc
            }, [])

            const {
              totalFlyersDistributed,
              totalMetricResults,
            } = missions.reduce(
              (acc, mission) => {
                acc.totalFlyersDistributed =
                  acc.totalFlyersDistributed + mission.totalFlyersDistributed

                mission.metrics.forEach(metric => {
                  const { metricId, conversionsCount } = metric

                  if (acc.totalMetricResults[metricId]) {
                    acc.totalMetricResults[metricId] =
                      acc.totalMetricResults[metricId] + conversionsCount
                  } else {
                    acc.totalMetricResults[metricId] = conversionsCount
                  }
                })

                return acc
              },
              {
                totalFlyersDistributed: 0,
                totalMetricResults: {},
              },
            )

            const missionsMetrics = metricsList.map(
              ({ id, name, isPrimary }) => {
                const totalConversions =
                  Object.values(totalMetricResults).length &&
                  totalMetricResults[id]
                    ? totalMetricResults[id]
                    : 0

                const hardCosts = missions.reduce(
                  (acc, { hardCosts }) => (acc += hardCosts),
                  0,
                )
                const conversionRate =
                  totalFlyersDistributed > 0
                    ? (totalConversions / totalFlyersDistributed) * 100 // Multiplied by 100 for a percentage value
                    : null

                const costPerMetric =
                  hardCosts && totalConversions
                    ? hardCosts / totalConversions
                    : null

                return {
                  id,
                  name,
                  isPrimary,
                  metricValue: totalConversions,
                  conversionRate: conversionRate ? conversionRate : 0,
                  costPerMetric: costPerMetric ? costPerMetric : 0,
                }
              },
            )

            const metricsCount = missionsMetrics.reduce(
              (acc, { name, metricValue, costPerMetric, conversionRate }) => {
                acc[name] =
                  metricValue || costPerMetric || conversionRate
                    ? ++acc[name] || 0
                    : 0
                return acc
              },
              {},
            )
            const mergedMissionMetrics = missionsMetrics.reduce(
              (acc, { name, metricValue, costPerMetric, conversionRate }) => ({
                ...acc,
                [name]: {
                  name,
                  metricValue:
                    acc[name] && acc[name].metricValue
                      ? acc[name].metricValue + metricValue
                      : metricValue,
                  costPerMetric:
                    acc[name] && acc[name].costPerMetric
                      ? acc[name].costPerMetric + costPerMetric
                      : costPerMetric,
                  conversionRate:
                    acc[name] && acc[name].conversionRate
                      ? acc[name].conversionRate + conversionRate
                      : conversionRate,
                },
              }),
              {},
            )

            const calculateDuplicatedMetrics = Object.entries(
              mergedMissionMetrics,
            ).reduce(
              (acc, [name, metric]) =>
                metricsCount[name]
                  ? {
                      ...acc,
                      [name]: {
                        ...metric,
                        costPerMetric:
                          metric.costPerMetric / (metricsCount[name] + 1),
                        conversionRate:
                          metric.conversionRate / (metricsCount[name] + 1),
                      },
                    }
                  : { ...acc, [name]: metric },
              {},
            )

            return {
              ...acc,
              selectedMetrics: [
                ...new Set(
                  acc.selectedMetrics
                    ? [...acc.selectedMetrics, ...Object.keys(metricsCount)]
                    : Object.keys(metricsCount),
                ),
              ],
              [month]: {
                metrics: calculateDuplicatedMetrics,
              },
            }
          },
          {},
        )

        return {
          ...acc,
          [year]: monthlyMetricsData,
        }
      },
      {},
    )
    return {
      ...acc,
      [type]: yearlyMetricsData,
    }
  }, {})

  const yearlyCampaignsData = Object.entries(
    completedMissionsGroupedByMonth,
  ).reduce((acc, [type, years]) => {
    const monthlyCampaignsData = Object.entries(years).reduce(
      (acc, [year, months]) => {
        const monthsData = Object.entries(months).reduce(
          (acc, [month, missions]) => {
            const missionsData = missions.reduce((acc, mission) => {
              const {
                totalFlyersDistributed = 0,
                amountScans = 0,
                printingCostPerFlyer = 0,
                hardCosts,
              } = mission
              const printingCostForMission =
                printingCostPerFlyer * totalFlyersDistributed
              return {
                totalFlyersDistributed:
                  (acc.totalFlyersDistributed
                    ? acc.totalFlyersDistributed
                    : 0) + totalFlyersDistributed,
                amountScans:
                  (acc.amountScans ? acc.amountScans : 0) + amountScans,
                hardCosts: (acc.hardCosts ? acc.hardCosts : 0) + hardCosts,
                printingCosts:
                  (acc.printingCosts ? acc.printingCosts : 0) +
                  printingCostForMission,
              }
            }, {})

            return {
              ...acc,
              [month]: {
                ...missionsData,
                metrics:
                  metricsValuesForGroupedMissions[type][year][month].metrics,
              },
            }
          },
          {},
        )

        return {
          ...acc,
          selectedMetrics: [
            ...new Set([
              ...(acc.selectedMetrics ? acc.selectedMetrics : []),
              ...metricsValuesForGroupedMissions[type][year].selectedMetrics,
            ]),
          ],
          [year]: monthsData,
        }
      },
      {},
    )

    const monthlyCampaignsDataByType = Object.keys(monthlyCampaignsData).length
      ? { [type]: monthlyCampaignsData }
      : {}

    return {
      ...acc,
      ...monthlyCampaignsDataByType,
    }
  }, {})
  return {
    yearlyCampaignsData,
    metricsValuesForGroupedMissions,
  }
}

export const getExportContent = ({
  yearlyCampaignsData,
  metricsValuesForGroupedMissions,
}) =>
  Object.entries(yearlyCampaignsData).reduce((acc, [type, years]) => {
    const selectedMetricsKey = Object.keys(years).filter(
      key => key !== 'selectedMetrics',
    )[0]

    if (!selectedMetricsKey) return ''

    const selectedMetrics =
      metricsValuesForGroupedMissions[type][selectedMetricsKey]
        .selectedMetrics || []
    const titles = `Month, Flyers Distributed, ${selectedMetrics.join(
      ',',
    )},Scans, Hard Cost, Printing Cost, ${selectedMetrics
      .map(metric => `CP ${metric}`)
      .join(',')},${selectedMetrics
      .map(metric => `${metric} CVR`)
      .join(',')},\n`

    const yearsContent = Object.entries(years).reduce((acc, [year, months]) => {
      if (year === 'selectedMetrics') return acc

      const monthsContent = Object.entries(months).reduce(
        (acc, [month, monthValues]) => {
          return (
            acc +
            '\n' +
            `${month},${escapeCsvString(
              formatNumber(monthValues.totalFlyersDistributed),
            )},${selectedMetrics
              .map(metric =>
                monthValues.metrics[metric]
                  ? formatCSVvalue(monthValues.metrics[metric].metricValue)
                  : '-',
              )
              .join(',')},${formatCSVvalue(monthValues.amountScans)},${
              monthValues.hardCosts
                ? `${formatCSVvalue(monthValues.hardCosts)}`
                : 0
            },${
              monthValues.printingCosts
                ? `${formatCSVvalue(monthValues.printingCosts)}`
                : 0
            },${selectedMetrics
              .map(metric =>
                monthValues.metrics[metric] &&
                monthValues.metrics[metric].costPerMetric
                  ? `${formatCSVvalue(
                      monthValues.metrics[metric].costPerMetric,
                    )}`
                  : 'N/A',
              )
              .join(',')},${selectedMetrics
              .map(metric =>
                monthValues.metrics[metric] &&
                monthValues.metrics[metric].conversionRate
                  ? `${formatCSVvalue(
                      monthValues.metrics[metric].conversionRate,
                    )}`
                  : 'N/A',
              )
              .join(',')}`
          )
        },
        '',
      )

      return acc + `${year.split('y')[0]},${monthsContent},\n`
    }, '')
    return acc + `${getMissionType(type)},\n` + titles + yearsContent + '\n,\n'
  }, '')
